import { Circle } from '@dealstack/frontend/shared/components/common/Circle';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon/Icon';
import { useTranslationInvestor } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { downloadFileFromAssets } from '@dealstack/frontend/shared/files/downloadFile';
import { Button, Divider, Flex, List, Text, Title } from '@mantine/core';
import { forwardRef, useMemo } from 'react';
import styled from 'styled-components';

interface Props {
	className?: string;
	onClose?: () => void;
	limitHeight?: boolean;
	isCertifiedCopiesOfDocumentsRequested: boolean;
}

export const getInstructionFileUrl = (isCertifiedCopiesOfDocumentsRequested: boolean): string => {
	return isCertifiedCopiesOfDocumentsRequested
		? '/context/investor/how-to-upload-documents/dealstack_how_to_provide_document_instruction(full).pdf'
		: '/context/investor/how-to-upload-documents/dealstack_how_to_provide_document_instruction.pdf';
};

const KycInstruction = forwardRef<HTMLDivElement, Props>(
	({ className, limitHeight, isCertifiedCopiesOfDocumentsRequested, onClose }, ref) => {
		const tInvestor = useTranslationInvestor();

		const instruction = useMemo(() => {
			return getInstructionFileUrl(isCertifiedCopiesOfDocumentsRequested);
		}, [isCertifiedCopiesOfDocumentsRequested]);

		return (
			<div className={className} ref={ref}>
				<Flex justify="space-between" align="center">
					<Text size="xl" fw={600} lh="2rem">
						{tInvestor('investment.join.kyc.instr.title')}
					</Text>
					{!!onClose && <Icon onClick={onClose} variant="minus" size="xxl" />}
				</Flex>
				<Divider />
				<Flex
					direction="column"
					gap="1rem"
					sx={
						limitHeight
							? { maxHeight: '85vh', overflowY: 'auto', overflowX: 'hidden' }
							: {}
					}
				>
					<Button
						onClick={() => {
							downloadFileFromAssets(instruction);
						}}
						variant="primary"
						leftSection={<Icon variant="download-v3" size="lg" />}
						size="sm"
						style={{ flexShrink: 0 }}
					>
						{tInvestor('investment.join.kyc.instr.download')}
					</Button>
					<Text size="sm">{tInvestor('investment.join.kyc.instr.steps_descr')}</Text>

					<Flex>
						<IconWithLabel>
							<Icon variant="user-profile" size="xl" />
							<Text size="sm" fw={600}>
								{tInvestor('investment.join.kyc.instr.your_proof_id')}
							</Text>
						</IconWithLabel>
						<IconWithLabel>
							<Icon variant="document-v3" size="xl" />
							<Text size="sm" fw={600}>
								{tInvestor('investment.join.kyc.instr.your_proof_address')}
							</Text>
						</IconWithLabel>
					</Flex>

					<Text size="sm">{tInvestor('investment.join.kyc.instr.follow_steps')}</Text>

					<Step title={tInvestor('investment.join.kyc.instr.collect')} number={1} />
					<SubStep
						letter="A"
						title={tInvestor('investment.join.kyc.instr.proof_of_identity')}
						description={tInvestor('investment.join.kyc.instr.you_need_identity')}
						subList={[
							tInvestor('investment.join.kyc.instr.passport'),
							tInvestor('investment.join.kyc.instr.id_card'),
							tInvestor('investment.join.kyc.instr.driver_license'),
							tInvestor('investment.join.kyc.instr.residence_permit'),
						]}
					/>
					<SubStep
						letter="B"
						title={tInvestor('investment.join.kyc.instr.proof_of_address')}
						description={tInvestor('investment.join.kyc.instr.you_need_address')}
						subList={[
							tInvestor('investment.join.kyc.instr.bill'),
							tInvestor('investment.join.kyc.instr.bank_statement'),
							tInvestor('investment.join.kyc.instr.letter_gov'),
							tInvestor('investment.join.kyc.instr.residence_permit'),
						]}
					/>
					<SubStep
						letter="C"
						title={tInvestor('investment.join.kyc.instr.additionally_for_piv')}
						description={tInvestor(
							'investment.join.kyc.instr.additionally_for_piv_needs'
						)}
						subList={[
							tInvestor('investment.join.kyc.instr.ownership'),
							tInvestor('investment.join.kyc.instr.existence'),
							tInvestor('investment.join.kyc.instr.laws'),
							tInvestor('investment.join.kyc.instr.sign_power'),
						]}
					/>

					<Step
						title={tInvestor('investment.join.kyc.instr.check_is_valid')}
						number={2}
					/>
					<SubStep
						letter="A"
						title={tInvestor('investment.join.kyc.instr.proof_of_identity')}
						description={tInvestor(
							'investment.join.kyc.instr.proof_of_identity_check_descr'
						)}
					/>
					<SubStep
						letter="B"
						title={tInvestor('investment.join.kyc.instr.proof_of_address')}
						description={tInvestor(
							'investment.join.kyc.instr.proof_of_address_check_descr'
						)}
					/>

					<Step title={tInvestor('investment.join.kyc.instr.scan_or_copy')} number={3} />
					<SubStep
						letter="A"
						description={tInvestor('investment.join.kyc.instr.scan_or_copy_descr')}
					/>

					{isCertifiedCopiesOfDocumentsRequested && (
						<>
							<Step
								title={tInvestor('investment.join.kyc.instr.find_who_can_certify')}
								number={4}
							/>
							<SubStep
								letter="A"
								description={tInvestor(
									'investment.join.kyc.instr.take_the_physical_copy'
								)}
							/>
							<SubStep
								letter="B"
								title={tInvestor('investment.join.kyc.instr.full_list_of_people')}
								subList={[
									tInvestor('investment.join.kyc.instr.judiciary'),
									tInvestor('investment.join.kyc.instr.officer'),
									tInvestor('investment.join.kyc.instr.lawyer'),
									tInvestor('investment.join.kyc.instr.individual_is_qualified'),
									tInvestor('investment.join.kyc.instr.director_officer'),
								]}
							/>

							<Step
								title={tInvestor('investment.join.kyc.instr.have_docs_certified')}
								number={5}
							/>
							<SubStep
								letter="A"
								title={tInvestor('investment.join.kyc.instr.prepare_for_signings')}
							/>
							<SubStep
								letter="B"
								title={tInvestor('investment.join.kyc.instr.proof_of_identity')}
								description={tInvestor(
									'investment.join.kyc.instr.poi_write_the_following'
								)}
							/>
							<SubStep
								letter="C"
								title={tInvestor('investment.join.kyc.instr.proof_of_address')}
								description={tInvestor(
									'investment.join.kyc.instr.poa_write_the_following'
								)}
							/>
							<SubStep
								letter="D"
								title={tInvestor('investment.join.kyc.instr.additionally_for_piv')}
								description={tInvestor(
									'investment.join.kyc.instr.piv_write_the_following'
								)}
							/>
							<SubStep
								letter="E"
								title={tInvestor('investment.join.kyc.instr.certifier_should_sign')}
								subList={[
									tInvestor('investment.join.kyc.instr.date_of_certification'),
									tInvestor('investment.join.kyc.instr.their_full_name'),
									tInvestor('investment.join.kyc.instr.their_profession'),
									tInvestor('investment.join.kyc.instr.their_contact_details'),
								]}
							/>

							<Step
								title={tInvestor('investment.join.kyc.instr.create_electronic')}
								number={6}
							/>
							<SubStep
								letter="A"
								description={tInvestor(
									'investment.join.kyc.instr.upload_scan_to_dealstack'
								)}
							/>
						</>
					)}
				</Flex>
			</div>
		);
	}
);

const Step = styled(
	({ className, title, number }: { className?: string; title: string; number: number }) => {
		return (
			<Flex align="center" gap="0.5rem" className={className}>
				<Circle variant="filled">
					<Text fz="lg" fw={600} lh="1.43rem" c="gray.0">
						{number}
					</Text>
				</Circle>
				<Title order={5} fw={600}>
					{title}
				</Title>
			</Flex>
		);
	}
)``;

const SubStep = styled(
	({
		className,
		title,
		description,
		letter,
		subList,
	}: {
		className?: string;
		title?: string;
		description?: string;
		letter: string;
		subList?: string[];
	}) => {
		return (
			<ListItem>
				<Flex gap="0.5rem">
					<Circle variant="outlined">
						<Text fz="sm" c="dark.8" fw={600} lh="1.43rem">
							{letter}
						</Text>
					</Circle>

					<Flex direction="column" gap="0.5rem" className={className}>
						{!!title && (
							<Text size="sm" fw={600}>
								{title}
							</Text>
						)}

						{!!description && <Text size="sm">{description}</Text>}

						{!!subList?.length && (
							<List listStyleType="disc">
								{subList.map((item) => (
									<List.Item key={item}>{item}</List.Item>
								))}
							</List>
						)}
					</Flex>
				</Flex>
			</ListItem>
		);
	}
)``;

const IconWithLabel = styled.div`
	display: flex;
	align-items: center;
	gap: 0.5rem;
	&:first-of-type {
		padding-right: 1rem;
		border-right: 1px solid ${({ theme }) => theme.v3.colors.gray[4]};
	}
	&:last-of-type {
		margin-left: 1rem;
	}
`;

const ListItem = styled.div`
	display: flex;
	flex-direction: column;
	gap: 0.25rem;
	margin-left: 2.5rem;
	padding-right: 1rem;
`;

const Styled = styled(KycInstruction)`
	max-width: 40rem;
	display: flex;
	flex-direction: column;
	gap: 1rem;
	padding: 1rem;
	border: 1px solid ${({ theme }) => theme.v3.colors.gray[4]};
	border-radius: 4px;
`;

export { Styled as KycInstruction };
